import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Play, ChevronDown, ChevronRight } from 'lucide-react';
import { salesApi } from '../api';
import ImportRunsExport from './ImportRunsExport';
import ErpDigestCard from './ErpDigestCard';

const fmtDT = (v) => {
  if (!v) return '—';
  const d = new Date(v.endsWith?.('Z') || v.includes?.('+') ? v : `${v}Z`);
  if (isNaN(d)) return '—';
  return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' IST';
};

const STATUS_STYLE = {
  success: 'bg-[#E6F2E9] text-[#2f6b41]',
  failed: 'bg-[#FBE7E4] text-[#9b2c1f]',
  skipped: 'bg-[#F3EFE2] text-[#7a6f5c]',
  running: 'bg-[#E8EEF8] text-[#2c4a7a]',
};

const StatusPill = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-[11.5px] font-medium capitalize ${STATUS_STYLE[status] || STATUS_STYLE.skipped}`}>{status}</span>
);

function RunRow({ run }) {
  const [open, setOpen] = useState(false);
  const errs = run.errors || [];
  const hasDetail = errs.length > 0 || run.errorSummary;
  return (
    <>
      <tr data-testid={`import-run-row-${run.id}`} className="border-t border-[#EFEADB] align-top">
        <td className="px-3 py-2">
          {hasDetail ? (
            <button data-testid={`import-run-toggle-${run.id}`} onClick={() => setOpen(o => !o)} className="text-[#7a6f5c]">
              {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : null}
        </td>
        <td className="px-3 py-2 whitespace-nowrap">{fmtDT(run.startedAt)}</td>
        <td className="px-3 py-2 capitalize">{run.trigger}</td>
        <td className="px-3 py-2" data-testid={`import-run-status-${run.id}`}><StatusPill status={run.status} /></td>
        <td className="px-3 py-2 font-mono text-[12px] max-w-[220px] truncate" title={run.attachmentName || ''}>{run.attachmentName || '—'}</td>
        <td className="px-3 py-2 text-right">{run.total ?? '—'}</td>
        <td className="px-3 py-2 text-right">{run.written ?? '—'}</td>
        <td className="px-3 py-2 text-[12.5px] text-[#7a6f5c] max-w-[320px] truncate" title={run.errorSummary || ''}>{run.errorSummary || (errs.length ? `${errs.length} row error(s)` : '—')}</td>
      </tr>
      {open && (
        <tr data-testid={`import-run-detail-${run.id}`} className="bg-[#FBF9F3]">
          <td />
          <td colSpan={7} className="px-3 py-3 text-[12.5px]">
            {run.errorSummary && <div className="mb-2 text-[#9b2c1f] break-words">{run.errorSummary}</div>}
            {run.emailSubject && <div className="mb-2 text-[#7a6f5c]">Email: {run.emailSubject}</div>}
            {errs.length > 0 && (
              <ul className="space-y-1 max-h-[240px] overflow-auto">
                {errs.map((e, i) => <li key={i}><span className="font-mono">Row {e.row}</span>: {e.reason}</li>)}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function ImportRunsPanel({ theme }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [running, setRunning] = useState(false);

  const load = async () => {
    setErr('');
    try { setRows(await salesApi.importRuns()); }
    catch (e) { setErr(e.response?.data?.detail || 'Failed to load'); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const syncNow = async () => {
    setRunning(true);
    try { await salesApi.syncNow(); }
    catch (e) { alert(e.response?.data?.detail || 'Sync failed'); }
    finally { setRunning(false); load(); }
  };

  return (
    <div className="fm-card p-5" data-testid="import-runs-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[14px] font-medium">Sales import runs</div>
          <p className="text-[12.5px] text-[#7a6f5c]">Last 20 automatic (6 AM / 6 PM IST) and manual syncs from the ERP mailbox.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="import-runs-refresh-btn" onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]">
            <RefreshCw size={14} /> Refresh
          </button>
          <button data-testid="import-runs-sync-now-btn" onClick={syncNow} disabled={running} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-white text-[13px] font-medium disabled:opacity-60" style={{ background: theme.primary }}>
            {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} {running ? 'Syncing…' : 'Run sync now'}
          </button>
        </div>
      </div>
      {err && <div data-testid="import-runs-error" className="text-[13px] text-[#9b2c1f] mb-3">{err}</div>}
      <ErpDigestCard theme={theme} />
      <ImportRunsExport />
      {rows === null ? (
        <div className="py-8 text-center"><Loader2 className="animate-spin inline" size={18} /></div>
      ) : rows.length === 0 ? (
        <div data-testid="import-runs-empty" className="py-8 text-center text-[13px] text-[#7a6f5c]">No sync runs yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="import-runs-table">
            <thead>
              <tr className="text-left text-[12px] text-[#7a6f5c]">
                <th className="px-3 py-2 w-6" /><th className="px-3 py-2">Started</th><th className="px-3 py-2">Trigger</th>
                <th className="px-3 py-2">Status</th><th className="px-3 py-2">Attachment</th>
                <th className="px-3 py-2 text-right">Rows</th><th className="px-3 py-2 text-right">Written</th><th className="px-3 py-2">Notes</th>
              </tr>
            </thead>
            <tbody>{rows.map(r => <RunRow key={r.id} run={r} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
